import { mediaQueryLarge, startViewTransition } from '@theme/utilities';
import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

const SESSION_STORAGE_PREFIX = 'plp-batches:';

/**
 * A custom element that renders a paginated results list with a manual "Load more" control.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLButtonElement} [loadMoreButton] - The "Load more" button.
 * @property {HTMLElement} [productCounter] - The "Viewing X out of Y products" element.
 * @property {HTMLElement} [liveRegion] - The aria-live region announcing newly loaded products.
 * @property {HTMLElement[]} [cards] - The card elements currently in the grid.
 *
 * @extends Component<Refs>
 */
export default class ResultsList extends Component {
  /** @type {boolean} */
  #isLoading = false;

  /** @type {number} */
  #generation = 0;

  /** @type {HTMLElement | null} */
  #placeholder = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.addEventListener('pagehide', this.#persistState);

    this.#restore();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.removeEventListener('pagehide', this.#persistState);
  }

  /**
   * Loads the next batch of products. Invoked by clicking the "Load more" button.
   */
  loadNextBatch = async () => {
    if (this.#isLoading) return;

    const { grid } = this.refs;
    if (!grid) return;

    const currentPage = Number(grid.dataset.currentPage || 1);
    const lastPage = Number(grid.dataset.lastPage || 1);
    const nextPage = currentPage + 1;

    if (nextPage > lastPage) return;

    const generation = this.#generation;
    this.#setLoading(true);
    this.#reserveSpace();

    try {
      const cards = await this.#fetchPageCards(nextPage);
      if (generation !== this.#generation || !cards.length) return;

      grid.append(...cards);
      grid.dataset.currentPage = String(nextPage);

      this.#aspectRatioHelper?.processNewElements();
      this.#updateCounter();
      this.#updateButtonVisibility();
      this.#announceLoaded(cards.length);
      this.#focusFirst(cards);
      this.#persistState();
    } finally {
      this.#releaseSpace();
      if (generation === this.#generation) this.#setLoading(false);
    }
  };

  /**
   * Restores state on connect: either backfills the pages preceding a deep-linked
   * page (e.g. `?page=3`), or, only when this document load is an actual browser
   * back/forward navigation, restores previously loaded batches from sessionStorage
   * (e.g. returning from a PDP via the browser back button). A fresh visit to the
   * collection or a plain reload always resets to a single batch.
   */
  async #restore() {
    const { grid } = this.refs;
    if (!grid) return;

    const currentPage = Number(grid.dataset.currentPage || 1);

    if (currentPage > 1) {
      await this.#backfillPreviousPages(currentPage);
    } else if (this.#isBackForwardNavigation()) {
      this.#restoreFromSession();
    } else {
      this.#clearStoredState();
    }

    this.#scrollToAnchor();
  }

  /**
   * @returns {boolean} Whether this document load is a browser back/forward navigation,
   * as opposed to a fresh navigation or a reload.
   */
  #isBackForwardNavigation() {
    try {
      const [entry] = performance.getEntriesByType('navigation');
      if (entry) return entry.type === 'back_forward';
      // Fallback for browsers without the Navigation Timing Level 2 API.
      if (performance.navigation) return performance.navigation.type === 2;
    } catch {
      // Ignore and fall through to the safe default below.
    }

    return false;
  }

  /**
   * Fetches and prepends every page before a deep-linked page, so a direct visit to
   * `?page=N` looks the same as if the visitor had used "Load more" N-1 times.
   *
   * @param {number} currentPage
   */
  async #backfillPreviousPages(currentPage) {
    const { grid } = this.refs;
    if (!grid || currentPage <= 1) return;

    const generation = this.#generation;
    this.#setHidden(true);

    try {
      const pageNumbers = Array.from({ length: currentPage - 1 }, (_, index) => index + 1);
      const results = await Promise.all(pageNumbers.map((page) => this.#fetchPageCards(page)));
      if (generation !== this.#generation) return;

      for (let index = results.length - 1; index >= 0; index--) {
        const cards = results[index];
        if (cards.length) grid.prepend(...cards);
      }

      this.#aspectRatioHelper?.processNewElements();
      this.#persistState();
    } finally {
      this.#setHidden(false);
    }
  }

  /**
   * Synchronously restores previously loaded batches from sessionStorage (no network
   * request), so returning from a PDP shows the same batches with no flash of batch 1.
   */
  #restoreFromSession() {
    const { grid } = this.refs;
    if (!grid) return;

    const stored = this.#readStoredState();
    if (!stored || !stored.lastPage || stored.lastPage <= 1) return;

    const currentPage = Number(grid.dataset.currentPage || 1);
    if (stored.lastPage <= currentPage) return;

    this.#setHidden(true);

    try {
      for (let page = currentPage + 1; page <= stored.lastPage; page++) {
        const html = stored.pages?.[page];
        if (!html) continue;
        grid.append(...this.#deserializeCards(html));
      }

      grid.dataset.currentPage = String(stored.lastPage);
      this.#aspectRatioHelper?.processNewElements();
      this.#updateCounter();
      this.#updateButtonVisibility();
    } finally {
      this.#setHidden(false);
    }
  }

  /**
   * Scrolls to the product card the visitor clicked into on the PDP, if any.
   */
  #scrollToAnchor() {
    const { hash } = window.location;
    if (!hash) return;

    const target = document.getElementById(hash.slice(1));
    target?.scrollIntoView({ behavior: 'instant', block: 'start' });
  }

  /**
   * Fetches a single page of results and returns its card elements.
   *
   * @param {number} pageNumber
   * @returns {Promise<HTMLElement[]>}
   */
  async #fetchPageCards(pageNumber) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(pageNumber));
    url.hash = '';

    const html = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
    const parsedPage = new DOMParser().parseFromString(html, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return [];

    return Array.from(gridElement.querySelectorAll(':scope > [ref="cards[]"]'));
  }

  /**
   * Reserves visual space for the incoming batch to avoid a layout shift while it loads.
   */
  #reserveSpace() {
    const { grid } = this.refs;
    if (!grid) return;

    const lastCard = grid.lastElementChild;
    if (!(lastCard instanceof HTMLElement)) return;

    const cardHeight = lastCard.getBoundingClientRect().height;
    if (!cardHeight) return;

    const columns = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length || 1;
    const pageSize = Number(grid.dataset.pageSize || 0);
    const totalItems = Number(grid.dataset.totalItems || 0);
    const currentPage = Number(grid.dataset.currentPage || 1);
    const remaining = totalItems - currentPage * pageSize;
    const batchCount = Math.max(0, Math.min(pageSize, remaining));
    if (!batchCount) return;

    const rows = Math.ceil(batchCount / columns);

    const placeholder = document.createElement('li');
    placeholder.className = 'product-grid__load-more-placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.style.minHeight = `${rows * cardHeight}px`;
    grid.append(placeholder);
    this.#placeholder = placeholder;
  }

  #releaseSpace() {
    this.#placeholder?.remove();
    this.#placeholder = null;
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    this.#isLoading = isLoading;

    const { loadMoreButton } = this.refs;
    if (!loadMoreButton) return;

    loadMoreButton.disabled = isLoading;
    loadMoreButton.setAttribute('aria-busy', String(isLoading));

    const label = isLoading
      ? loadMoreButton.getAttribute('data-label-loading')
      : loadMoreButton.getAttribute('data-label-default');
    if (label) loadMoreButton.textContent = label;
  }

  /**
   * @param {boolean} hidden
   */
  #setHidden(hidden) {
    this.style.visibility = hidden ? 'hidden' : '';
  }

  #updateButtonVisibility() {
    const { grid, loadMoreButton } = this.refs;
    if (!grid || !loadMoreButton) return;

    const currentPage = Number(grid.dataset.currentPage || 1);
    const lastPage = Number(grid.dataset.lastPage || 1);
    loadMoreButton.hidden = currentPage >= lastPage;
  }

  #updateCounter() {
    const { grid, productCounter } = this.refs;
    if (!grid || !productCounter) return;

    const currentPage = Number(grid.dataset.currentPage || 1);
    const pageSize = Number(grid.dataset.pageSize || 0);
    const totalItems = Number(grid.dataset.totalItems || 0);
    const shown = Math.min(currentPage * pageSize, totalItems);

    if (shown >= totalItems) {
      const template = productCounter.getAttribute('data-template-all');
      if (template) productCounter.textContent = template;
    } else {
      const template = productCounter.getAttribute('data-template-range');
      if (template) productCounter.textContent = template.replace('[to]', String(shown));
    }
  }

  /**
   * @param {number} count
   */
  #announceLoaded(count) {
    const { liveRegion } = this.refs;
    if (!liveRegion) return;

    const template = liveRegion.getAttribute('data-template-loaded');
    if (template) liveRegion.textContent = template.replace('[count]', String(count));
  }

  /**
   * @param {HTMLElement[]} cards
   */
  #focusFirst(cards) {
    const [firstCard] = cards;
    const focusable = firstCard?.querySelector('a, button, [tabindex]');
    if (focusable instanceof HTMLElement) focusable.focus({ preventScroll: true });
  }

  /**
   * @param {HTMLElement[]} cards
   * @returns {string}
   */
  #serializeCards(cards) {
    return cards.map((card) => card.outerHTML).join('');
  }

  /**
   * @param {string} html
   * @returns {HTMLElement[]}
   */
  #deserializeCards(html) {
    const container = document.createElement('ul');
    container.innerHTML = html;
    return Array.from(container.children).filter((child) => child instanceof HTMLElement);
  }

  /**
   * @returns {string}
   */
  #storageKey() {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    params.delete('page');
    params.sort();
    return `${SESSION_STORAGE_PREFIX}${url.pathname}?${params.toString()}`;
  }

  /**
   * @returns {{ lastPage: number, pages: Record<string, string> } | null}
   */
  #readStoredState() {
    try {
      const raw = sessionStorage.getItem(this.#storageKey());
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  /**
   * Snapshots every loaded batch (beyond page 1) into sessionStorage, so that returning
   * from the PDP via the browser back button can restore them without a network request.
   */
  #persistState = () => {
    const { grid, cards } = this.refs;
    if (!grid) return;

    const lastPage = Number(grid.dataset.currentPage || 1);

    if (lastPage <= 1 || !Array.isArray(cards)) {
      this.#clearStoredState();
      return;
    }

    /** @type {Map<string, HTMLElement[]>} */
    const cardsByPage = new Map();
    for (const card of cards) {
      const page = card.dataset.page;
      if (!page || Number(page) <= 1) continue;

      const pageCards = cardsByPage.get(page) ?? [];
      pageCards.push(card);
      cardsByPage.set(page, pageCards);
    }

    /** @type {Record<string, string>} */
    const pages = {};
    for (const [page, pageCards] of cardsByPage) {
      pages[page] = this.#serializeCards(pageCards);
    }

    try {
      sessionStorage.setItem(this.#storageKey(), JSON.stringify({ lastPage, pages }));
    } catch {
      // Ignore storage errors (quota exceeded, private mode, etc.) — restoration is
      // a progressive enhancement, not required for the page to function.
    }
  };

  #clearStoredState() {
    try {
      sessionStorage.removeItem(this.#storageKey());
    } catch {
      // Ignore storage errors.
    }
  }

  /**
   * Handles filter/sort updates: cancels any pending load and cached batches, since
   * they belong to a result set that no longer applies.
   */
  #handleFilterUpdate = () => {
    this.#generation++;
    this.#isLoading = false;
    this.#releaseSpace();
    this.#clearStoredState();
  };

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
